import { debounce } from 'lodash-es';
import SignaturePad from 'signature_pad';
import { closeAllMenus, closeAllDrawers } from 'modules/shared/overflow-menu';
import { createNotice } from 'modules/shared/notice';

// The one SignaturePad instance for whichever "review" document is currently
// open in the viewer -- torn down (see closeSignaturePad) on every close/reopen
// so it never keeps drawing into a canvas the modal has since thrown away.
let activeSignaturePad = null;

// Rebuilt fresh on every turbolinks:load (see bindRowSelection) -- "type:id" ->
// the row's <tr> element. Module-level (not local to bindRowSelection) so the
// row-open bindings below and the toolbar action bindings can all read/update
// the same live selection without threading it through every function.
let selectedRows = new Map();

// Coarse-pointer devices (phones/tablets) don't have a discoverable double-tap
// gesture, so rows open on a single tap there (unless a selection is already
// active) and a long-press enters selection mode instead. Recomputed once per
// turbolinks:load rather than per-row.
let isTouchDevice = false;

// { childrenByParent: Map<string|null, string[]>, allFolderIds: Set<string> } for
// the current site, or null on a page with no folder tree to check (or a bad
// JSON payload) -- rebuilt once per turbolinks:load in buildFolderMoveIndex,
// then read on every selection change by selectionHasMoveDestination below
// rather than re-parsed per check.
let folderMoveIndex = null;

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

// How long a desktop click waits before toggling selection, so a following
// dblclick has time to cancel it first -- see bindRowOpenAndSelect. Shorter
// than the browser's own double-click registration window, which trades off
// some robustness against unusually slow double-clicks for a snappier single
// click (the common case).
const CLICK_DELAY_MS = 300;

document.addEventListener("turbolinks:load", () => {
  isTouchDevice = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  folderMoveIndex = buildFolderMoveIndex();

  const sdModal = document.getElementById('site-docs-modal');
  const viewer = document.querySelector('.sd-viewerContainer');

  bindRowSelection();

  // File rows open on double click (single tap on touch, unless a selection is
  // active -- see bindRowOpenAndSelect) anywhere in the row, except over the
  // actions column so its own buttons/links keep their normal click behavior.
  // The "View File" overflow-menu action opens the same viewer but always
  // stays a single click, regardless of device or selection state.
  document.querySelectorAll('.c-fileRow.sd-view-doc').forEach(row => {
    const target = row.closest('tr') || row;

    bindRowOpenAndSelect(target, () => openDocViewer(row));
  });

  document.querySelectorAll('.sd-view-doc:not(.c-fileRow)').forEach(button => {
    button.onclick = (e) => {
      e.preventDefault();
      openDocViewer(button);
    };
  });

  document.querySelectorAll('[data-action="sd-close-doc"]').forEach(button => {
    button.onclick = (e) => {
      e.preventDefault();
      sdModal.style.display = 'none';
      viewer.innerHTML = "";
      closeSignaturePad();
    };
  });

  const uploadModal = document.getElementById('site-docs-upload-modal');
  const uploadForm = document.getElementById('upload-site-doc-form');
  const uploadFormContent = document.querySelector('.js-uploadSiteDocFormContent');
  const uploadLoading = document.querySelector('.js-uploadSiteDocLoading');

  if (uploadModal) {
    document.querySelectorAll('.js-upload-site-doc-form').forEach(button => {
      button.onclick = (e) => {
        e.preventDefault();
        closeAllMenus();
        closeAllDrawers();
        uploadModal.style.display = 'flex';
        uploadModal.scrollTop = 0;
      };
    });

    document.querySelectorAll('[data-action="sd-upload-hide-form"]').forEach(button => {
      button.onclick = (e) => {
        e.preventDefault();
        uploadForm.reset();
        uploadModal.style.display = 'none';
      };
    });

    // The form is a real (non-AJAX) POST -- the browser navigates away once the
    // response comes back, whether the upload fully succeeded, partially failed,
    // or errored entirely, so there's no case where this spinner is left stuck.
    uploadForm.addEventListener('submit', () => {
      uploadFormContent.classList.add('u-hide');
      uploadLoading.classList.remove('u-hide');
    });
  }

  bindFolderNavigation();
  bindNewFolderModal();
  bindRenameFolderModal();
  bindMoveToModal();
  bindDragAndDrop();
  bindHeadingCollapse();
  bindDownloadAllConfirm();
  bindExportSignatureReport();
  bindViewSignatureModal();
});

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : null;
}

function sitePaths() {
  const container = document.querySelector('[data-site-docs-path]');

  return container ? {
    siteDocs: container.dataset.siteDocsPath,
    siteDocumentFolders: container.dataset.siteDocumentFoldersPath,
    currentFolderId: container.dataset.currentFolderId,
    siteFolderPairs: container.dataset.siteFolderPairs,
  } : null;
}

// Mirrors App::SiteDocsController#folder_ids_with_move_destination's [id,
// parent_folder_id] pairs into a lookup the selection toolbar can check
// synchronously against whatever's currently selected -- unlike that
// per-request/per-row check, the toolbar has to answer this for an
// arbitrary, ever-changing combination of files/folders the server can't
// predict ahead of time, so it needs the whole site's folder tree client-side.
function buildFolderMoveIndex() {
  const paths = sitePaths();
  if (!paths || !paths.siteFolderPairs) return null;

  let pairs;
  try {
    pairs = JSON.parse(paths.siteFolderPairs);
  } catch (e) {
    return null;
  }

  const childrenByParent = new Map();
  const allFolderIds = new Set();

  pairs.forEach(([id, parentId]) => {
    allFolderIds.add(String(id));

    const key = parentId === null || parentId === undefined ? null : String(parentId);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(String(id));
  });

  return { childrenByParent, allFolderIds };
}

function descendantIdsFromIndex(folderId, childrenByParent) {
  const ids = [];
  let queue = childrenByParent.get(String(folderId)) || [];

  while (queue.length) {
    ids.push(...queue);
    queue = queue.flatMap(id => childrenByParent.get(String(id)) || []);
  }

  return ids;
}

// Same exclusion rule as the server (self + descendants of every selected
// folder, plus the folder currently being browsed) -- see
// App::SiteDocsController#folder_ids_with_move_destination. Files in the
// selection contribute no exclusion of their own. Fails open (true) if the
// index never loaded, so a parsing hiccup hides nothing that would otherwise
// be a legitimate action.
function selectionHasMoveDestination(items, currentFolderId) {
  if (!folderMoveIndex) return true;

  const { childrenByParent, allFolderIds } = folderMoveIndex;
  const excludedIds = new Set(currentFolderId ? [String(currentFolderId)] : []);

  items
    .filter(row => row.dataset.itemType === 'folder')
    .forEach(row => {
      excludedIds.add(String(row.dataset.itemId));
      descendantIdsFromIndex(row.dataset.itemId, childrenByParent).forEach(id => excludedIds.add(id));
    });

  for (const id of allFolderIds) {
    if (!excludedIds.has(id)) return true;
  }

  return false;
}

// Only rows on the operative's own document list carry a view-path (see
// users/site_users/show.html.erb's data-view-path) -- everywhere else this
// shared viewer gets opened from (admin previews, the dashboard sign prompt)
// has none, so this is a no-op there rather than recording a view on their
// behalf. Fire-and-forget: a failed ping shouldn't block viewing the file.
function markDocumentViewed(button) {
  if (!button.dataset.viewPath) return;

  fetch(button.dataset.viewPath, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
  }).catch(() => {});
}

// Self-contained (looks up the modal/viewer itself) rather than closing over
// them, since it's called both from the turbolinks:load handler's own row
// bindings and from the selection toolbar's View action below.
function openDocViewer(button) {
  const sdModal = document.getElementById('site-docs-modal');
  const viewer = document.querySelector('.sd-viewerContainer');
  if (!sdModal || !viewer) return;

  markDocumentViewed(button);

  // Clears any open dashboard prompt modal (see dashboard/_modals.html.erb)
  // out of the way first -- otherwise it stays stacked open (and, depending
  // on z-index, visually on top of) this viewer. modals.js owns that stack
  // and isn't otherwise reachable from this module, so this is a DOM event
  // rather than a direct call; it's a no-op wherever no dashboard modal is
  // open (i.e. everywhere except a "Continue" trigger inside one), so it's
  // safe to always fire here regardless of what triggered this viewer.
  document.dispatchEvent(new CustomEvent('modals:closeAll'));

  // Closes all overflow menus and mobile drawers upon choosing View Doc
  closeAllMenus();
  closeAllDrawers();

  const type = button.dataset.type;

  // Anything that isn't previewable in-app (docx, zip, tiff, mov, etc. -- see
  // SiteDocument#doc_viewer_type) just opens as a normal browser tab instead of
  // occupying the modal with nothing to show. This list must stay in sync with
  // every non-'other' value doc_viewer_type can return server-side.
  if (!['image', 'pdf', 'video', 'audio'].includes(type)) {
    window.open(button.dataset.path, '_blank', 'noopener');
    return;
  }

  document.getElementById("sd-fileName").textContent = button.dataset.name;

  viewer.innerHTML = "";

  let element;

  if (type === 'image') {
    element = document.createElement('img');
    element.setAttribute('src', button.dataset.path);
  } else if (type === 'pdf') {
    // Uses an iframe rather than <object type="application/pdf">: object relies on
    // browser plugin support for PDFs, which mobile browsers don't have, so it never
    // rendered there. An iframe just loads the URL, which mobile PDF viewers handle fine.
    element = document.createElement('iframe');
    element.setAttribute('src', `${button.dataset.path}#view=FitH`);
  } else {
    // 'video' or 'audio' -- both are just a native element with built-in controls.
    element = document.createElement(type);
    element.setAttribute('src', button.dataset.path);
    element.setAttribute('controls', '');
  }

  element.setAttribute('width', '100%');
  element.className = 'c-siteDocs__viewer';

  viewer.appendChild(element);

  // Must come after showing the modal, not before: renderSignatureArea sizes
  // the signature canvas off its parent's clientWidth, which is 0 while an
  // ancestor is still display: none -- sizing it first silently leaves the
  // canvas with a 0x0 drawing buffer (no error, it just can't render any
  // strokes, even though its CSS border/background still show up fine).
  sdModal.style.display = 'flex';
  sdModal.scrollTop = 0;

  renderSignatureArea(button);
}

// Below the document, for a "Review & Signature Required" document only --
// either a signature pad (not yet signed) or a plain notice (already signed).
// Rebuilt from the row's own data attributes every time the viewer opens,
// rather than left in the shared modal markup, since most opened documents
// need neither.
function renderSignatureArea(button) {
  const container = document.querySelector('.sd-signatureContainer');
  const padWrap = document.querySelector('.sd-signaturePadWrap');
  const signatureImage = document.getElementById('sd-signatureImage');
  const signedNotice = document.getElementById('sd-signedNotice');
  const loading = document.getElementById('sd-signatureLoading');
  const closeButton = document.querySelector('.js-sdCloseButton');
  const clearButton = document.querySelector('.js-sdClearSignature');
  const submitButton = document.querySelector('.js-sdSubmitSignature');
  if (!container || !padWrap || !signatureImage || !signedNotice || !loading || !closeButton || !clearButton || !submitButton) return;

  // Default/reset state: no signature pad, Close shown, Clear/Submit hidden,
  // spinner hidden -- matches a document that doesn't need a signature at all.
  // Re-evaluated below for the two cases that do.
  closeSignaturePad();
  padWrap.innerHTML = '';
  container.classList.add('u-hide');
  signatureImage.classList.add('u-hide');
  signatureImage.src = '';
  signedNotice.classList.add('u-hide');
  loading.classList.add('u-hide');
  closeButton.classList.remove('u-hide');
  clearButton.classList.add('u-hide');
  submitButton.classList.add('u-hide');

  if (button.dataset.requiresSignature !== 'true') return;

  if (button.dataset.signed === 'true') {
    if (button.dataset.signatureImageUrl) {
      signatureImage.src = button.dataset.signatureImageUrl;
      signatureImage.classList.remove('u-hide');
    }

    signedNotice.textContent = button.dataset.signedAt
      ? `You signed this document on ${button.dataset.signedAt}.`
      : 'You have already signed this document.';
    signedNotice.classList.remove('u-hide');
    return;
  }

  // Not yet signed -- show the signature pad, and swap Close for Clear/Submit
  // (signing it is the only way out of this state via the button bar) until
  // it's actually signed, at which point submitSignature re-runs this
  // function and it falls into the "signed" branch above instead.
  container.classList.remove('u-hide');
  closeButton.classList.add('u-hide');
  clearButton.classList.remove('u-hide');
  submitButton.classList.remove('u-hide');

  const canvas = document.createElement('canvas');
  canvas.className = 'c-siteDocs__signaturePad';
  padWrap.appendChild(canvas);

  sizeSignatureCanvas(canvas);
  activeSignaturePad = new SignaturePad(canvas);

  clearButton.onclick = () => activeSignaturePad && activeSignaturePad.clear();
  submitButton.onclick = () => submitSignature(button);
}

// signature_pad draws at the canvas's actual pixel dimensions, not whatever
// CSS gives it -- without matching that to the devicePixelRatio, a strokes on
// a HiDPI screen would land offset from wherever the pointer actually is.
function sizeSignatureCanvas(canvas) {
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  const cssWidth = canvas.parentElement ? canvas.parentElement.clientWidth : 300;

  canvas.width = cssWidth * ratio;
  canvas.height = 200 * ratio;
  canvas.style.width = '100%';
  canvas.style.height = '200px';
  canvas.getContext('2d').scale(ratio, ratio);
}

// Torn down on every modal close/reopen (see the sd-close-doc handler and
// renderSignatureArea above) so it never keeps listening on a canvas the
// modal has already thrown away.
function closeSignaturePad() {
  if (!activeSignaturePad) return;

  activeSignaturePad.off();
  activeSignaturePad = null;
}

// A signature pad happily accepts a single tap or tiny scribble as a
// "signature" -- these can't verify who drew it (login already establishes
// that), but they can reject something that clearly isn't a deliberate
// signing gesture. Mirrors SiteDocumentSignature's own server-side check
// (see MIN_SIGNATURE_POINTS/MIN_BOUNDING_BOX_DIAGONAL there) -- this is only
// the fast client-side rejection; the server independently re-validates the
// same raw stroke data, since this endpoint can be posted to directly,
// bypassing this check entirely.
const MIN_SIGNATURE_POINTS = 10;
const MIN_BOUNDING_BOX_DIAGONAL = 40;

function isDeliberateSignature(strokeData) {
  const points = strokeData.flatMap(stroke => stroke.points || []);
  if (points.length < MIN_SIGNATURE_POINTS) return false;

  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const diagonal = Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));

  return diagonal >= MIN_BOUNDING_BOX_DIAGONAL;
}

// Toggles between "the pad + Clear/Submit are usable" and "a request is in
// flight" -- without this, submitting just disables the Submit button, which
// is easy to miss and reads as the page having frozen (nothing else on
// screen visibly changes while the fetch is pending).
function toggleSignatureSubmitting(isSubmitting) {
  const container = document.querySelector('.sd-signatureContainer');
  const loading = document.getElementById('sd-signatureLoading');
  const clearButton = document.querySelector('.js-sdClearSignature');
  const submitButton = document.querySelector('.js-sdSubmitSignature');
  if (!container || !loading || !clearButton || !submitButton) return;

  container.classList.toggle('u-hide', isSubmitting);
  loading.classList.toggle('u-hide', !isSubmitting);
  clearButton.classList.toggle('u-hide', isSubmitting);
  submitButton.classList.toggle('u-hide', isSubmitting);
  submitButton.disabled = isSubmitting;
}

function removeSignedActionItem(signPath) {
  const link = document.querySelector(`.qa-action-items [data-sign-path="${signPath}"]`);
  const item = link && link.closest('li');
  if (!item) return;

  const actionItems = item.closest('.qa-action-items');
  item.remove();

  const remaining = actionItems.querySelectorAll('li').length;
  if (remaining === 0) {
    actionItems.remove();
    return;
  }

  const pill = actionItems.querySelector('.c-actionItems__headerLeft .c-pill');
  if (pill) pill.textContent = remaining;
}

function submitSignature(button) {
  if (!activeSignaturePad || activeSignaturePad.isEmpty()) {
    window.alert('Please sign in the box before submitting.');
    return;
  }

  const strokeData = activeSignaturePad.toData();

  if (!isDeliberateSignature(strokeData)) {
    window.alert("We weren't able to capture your signature clearly. Please try again.");
    return;
  }

  toggleSignatureSubmitting(true);

  fetch(button.dataset.signPath, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      signature_data_url: activeSignaturePad.toDataURL('image/png'),
      signature_points: JSON.stringify(strokeData),
    }),
  })
    .then(res => res.json().then(data => ({ ok: res.ok, data })))
    .then(({ ok, data }) => {
      if (!ok || !data.success) {
        toggleSignatureSubmitting(false);
        window.alert((data.errors || ['Something went wrong submitting your signature.']).join(' '));
        return;
      }

      // Updates the row's own dataset so reopening this document later (without
      // a full page reload) already reflects the signed state. renderSignatureArea
      // takes it from here -- it already resets the spinner/pad/buttons to the
      // (now "signed") state, so there's nothing left for toggleSignatureSubmitting to undo.
      button.dataset.signed = 'true';
      button.dataset.signedAt = data.signed_at;
      button.dataset.signatureImageUrl = data.signature_image_url;

      renderSignatureArea(button);
      createNotice('Signature submitted. Thank you.');
      removeSignedActionItem(button.dataset.signPath);
    })
    .catch(() => {
      toggleSignatureSubmitting(false);
      window.alert('Something went wrong submitting your signature. Please try again.');
    });
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

// Bound once ever (not per turbolinks:load) to avoid piling up a fresh resize
// listener on every navigation -- the handler itself re-queries the current
// page's heading each time it fires, so it still reacts correctly afterwards.
let headingResizeListenerBound = false;

// Built via the DOM API (not innerHTML) since move-to folder rows are otherwise
// built with textContent -- keeps folder names from ever being parsed as markup.
function folderIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'c-fileRow__icon');
  svg.setAttribute('aria-label', 'folder');

  const use = document.createElementNS(SVG_NS, 'use');
  use.setAttributeNS(XLINK_NS, 'xlink:href', '/packs/sprite.svg#folder');

  svg.appendChild(use);
  return svg;
}

function showOverlay(id) {
  const el = document.getElementById(id);
  if (el) {
    el.style.display = 'flex';
    el.scrollTop = 0;
  }
}

function hideOverlay(id) {
  const el = document.getElementById(id);
  if (el) el.style.display = 'none';
}

// Shared by the folder-row and file-row open bindings, since both rows need the
// exact same interaction model, just with a different "what does opening this
// row mean" callback:
//  - desktop: a plain click toggles selection, dblclick always opens
//  - touch: a tap opens unless a selection is already active, in which case it
//    toggles instead; a long-press always toggles, entering selection mode
//    from empty
function bindRowOpenAndSelect(target, openFn) {
  if (!isTouchDevice) {
    // A dblclick fires as click, click, dblclick -- toggling selection
    // straight from the click handler would flash the row selected (and the
    // toolbar open) for the instant between those two clicks, every time.
    // Delaying the toggle lets the second click's dblclick handler cancel it
    // before it ever runs, so a genuine double click never toggles at all.
    let clickTimer = null;

    target.addEventListener('click', (e) => {
      if (e.target.closest('.c-table__actions')) return;
      e.preventDefault();

      // Clears the first click's own still-pending timer rather than just
      // overwriting the reference to it -- otherwise a double click (which
      // fires click, click, dblclick) leaves that first timer orphaned:
      // dblclick's handler only ever sees (and cancels) the second click's
      // timer, so the first one fires anyway ~CLICK_DELAY_MS after the whole
      // gesture, toggling selection after the document has already opened.
      if (clickTimer) window.clearTimeout(clickTimer);

      clickTimer = window.setTimeout(() => {
        clickTimer = null;
        toggleRowSelection(target);
      }, CLICK_DELAY_MS);
    });

    target.addEventListener('dblclick', (e) => {
      if (e.target.closest('.c-table__actions')) return;
      e.preventDefault();

      if (clickTimer) {
        window.clearTimeout(clickTimer);
        clickTimer = null;
      }

      openFn();
    });

    return;
  }

  let longPressTimer = null;
  let longPressTriggered = false;
  let touchStart = null;

  target.addEventListener('touchstart', (e) => {
    if (e.target.closest('.c-table__actions')) return;

    longPressTriggered = false;
    const touch = e.touches[0];
    touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;

    longPressTimer = window.setTimeout(() => {
      longPressTriggered = true;
      toggleRowSelection(target);
    }, LONG_PRESS_MS);
  });

  target.addEventListener('touchmove', (e) => {
    if (!touchStart || !longPressTimer) return;
    const touch = e.touches[0];
    if (!touch) return;

    const moved = Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y);
    if (moved > LONG_PRESS_MOVE_TOLERANCE_PX) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  });

  target.addEventListener('touchend', () => {
    if (longPressTimer) window.clearTimeout(longPressTimer);
  });

  target.addEventListener('click', (e) => {
    if (e.target.closest('.c-table__actions')) return;

    // The long-press that just fired already toggled selection -- the tap
    // that ends the press shouldn't also open or re-toggle it.
    if (longPressTriggered) {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    if (selectedRows.size > 0) {
      toggleRowSelection(target);
    } else {
      openFn();
    }
  });
}

function rowKey(row) {
  return `${row.dataset.itemType}:${row.dataset.itemId}`;
}

function toggleRowSelection(row) {
  const key = rowKey(row);

  if (selectedRows.has(key)) {
    selectedRows.delete(key);
    row.classList.remove('is-selected');
  } else {
    selectedRows.set(key, row);
    row.classList.add('is-selected');
  }

  renderSelectionToolbar();
}

function clearRowSelection() {
  selectedRows.forEach(row => row.classList.remove('is-selected'));
  selectedRows.clear();
  renderSelectionToolbar();
}

// Toggles via inline style rather than the `hidden` attribute -- `hidden` and
// a class selector like .c-selectionToolbar/.c-selectionToolbar__action have
// the same specificity, so that class's own `display` declaration (loaded
// after the browser's default `[hidden] { display: none }`) would otherwise
// win and keep the element visible. Matches showOverlay/hideOverlay's use of
// inline style below for the same reason. An empty string (rather than a
// hardcoded value) restores whatever `display` the stylesheet itself sets --
// e.g. .c-searchFilters varies its `display` by breakpoint, so hardcoding
// 'flex'/'grid' here would clobber that.
function setVisible(el, visible) {
  if (el) el.style.display = visible ? '' : 'none';
}

function toggleToolbarAction(toolbar, selector, show) {
  setVisible(toolbar.querySelector(selector), show);
}

// Shows/hides the toolbar itself (vs. the search/filter header) and each of
// its action icons based on the live selection -- every selected row's own
// data-can-destroy/data-can-update/data-deletable (baked in from each row's
// Pundit policy checks at render time, see _table.html.erb) decides what's
// legal for the *whole* selection, since these policies are fully per-record.
function renderSelectionToolbar() {
  const toolbar = document.querySelector('.js-selectionToolbar');
  if (!toolbar) return;

  const filters = document.querySelector('.c-searchFilters');
  const items = Array.from(selectedRows.values());
  const hasSelection = items.length > 0;

  setVisible(toolbar, hasSelection);
  setVisible(filters, !hasSelection);
  if (!hasSelection) return;

  const countEl = toolbar.querySelector('.js-selectionCount');
  if (countEl) countEl.textContent = `${items.length} selected`;

  const isFolder = (row) => row.dataset.itemType === 'folder';
  const canDestroy = items.every(row => row.dataset.canDestroy === 'true' && (!isFolder(row) || row.dataset.deletable === 'true'));
  const canUpdate = items.every(row => row.dataset.canUpdate === 'true');
  // Every row in the table already passed policy(document/folder).show? (always
  // true) to be rendered at all, so any selection -- files, folders, or a mix --
  // can always be zipped and downloaded; see bindSelectionToolbarActions below.
  const canDownload = true;
  const single = items.length === 1 ? items[0] : null;

  const canEdit = Boolean(single && !isFolder(single) && single.dataset.canUpdate === 'true' && single.querySelector('.js-rowEditLink'));
  const canRename = Boolean(single && isFolder(single) && single.dataset.canUpdate === 'true' && single.querySelector('.js-openRenameModal'));
  const canView = Boolean(single && !isFolder(single) && single.querySelector('.c-fileRow.sd-view-doc'));
  // Both single-file-only, mirroring whatever that row's own overflow menu
  // actually renders (View File Details / Resend Reminder), so the toolbar
  // never offers -- or hides -- more than that row's menu already does. Not
  // meaningful bulk actions, so neither ever shows once more than one row is
  // selected (single is null there already).
  const canShowDetails = Boolean(single && single.querySelector('.js-rowShowLink'));
  const canResendReminder = Boolean(single && single.querySelector('.js-rowResendReminderLink'));
  const currentFolderId = sitePaths()?.currentFolderId;
  const canMove = canUpdate && selectionHasMoveDestination(items, currentFolderId);

  toggleToolbarAction(toolbar, '.js-selectionDelete', canDestroy);
  toggleToolbarAction(toolbar, '.js-selectionMove', canMove);
  toggleToolbarAction(toolbar, '.js-selectionDownload', canDownload);
  toggleToolbarAction(toolbar, '.js-selectionEdit', canEdit);
  toggleToolbarAction(toolbar, '.js-selectionRename', canRename);
  toggleToolbarAction(toolbar, '.js-selectionView', canView);
  toggleToolbarAction(toolbar, '.js-selectionShowDetails', canShowDetails);
  toggleToolbarAction(toolbar, '.js-selectionResendReminder', canResendReminder);

  // Hierarchy restrictions (see SiteDocumentOwnershipRank) can leave a selection
  // -- typically a restricted folder -- with every action above hidden; this pill
  // fills that same slot so the toolbar doesn't just go blank.
  const hasAnyAction = canDestroy || canUpdate || canDownload || canEdit || canRename || canView || canShowDetails || canResendReminder;
  toggleToolbarAction(toolbar, '.js-selectionNoActions', !hasAnyAction);
}

// Binds the toolbar's own controls once per turbolinks:load -- each handler
// reads `selectedRows` fresh at click time rather than from a snapshot taken
// when the listener was attached, so this doesn't need to be re-bound whenever
// the selection (and so renderSelectionToolbar's show/hide) changes.
function bindSelectionToolbarActions() {
  const toolbar = document.querySelector('.js-selectionToolbar');
  if (!toolbar) return;

  const closeBtn = toolbar.querySelector('.js-clearSelection');
  if (closeBtn) {
    closeBtn.onclick = (e) => {
      e.preventDefault();
      clearRowSelection();
    };
  }

  const deleteBtn = toolbar.querySelector('.js-selectionDelete');
  if (deleteBtn) {
    deleteBtn.onclick = (e) => {
      e.preventDefault();

      const items = Array.from(selectedRows.values());
      if (items.length === 0) return;

      const confirmMessage = `Delete ${items.length} item${items.length === 1 ? '' : 's'}? This cannot be undone.`;
      if (!window.confirm(confirmMessage)) return;

      performBulkDestroy(items.map(row => ({ type: row.dataset.itemType, id: row.dataset.itemId })));
    };
  }

  const editBtn = toolbar.querySelector('.js-selectionEdit');
  if (editBtn) {
    editBtn.onclick = (e) => {
      e.preventDefault();
      const [row] = selectedRows.values();
      const link = row && row.querySelector('.js-rowEditLink');
      if (link) link.click();
    };
  }

  const renameBtn = toolbar.querySelector('.js-selectionRename');
  if (renameBtn) {
    renameBtn.onclick = (e) => {
      e.preventDefault();
      const [row] = selectedRows.values();
      const link = row && row.querySelector('.js-openRenameModal');
      if (link) link.click();
    };
  }

  const viewBtn = toolbar.querySelector('.js-selectionView');
  if (viewBtn) {
    viewBtn.onclick = (e) => {
      e.preventDefault();
      const [row] = selectedRows.values();
      const fileRow = row && row.querySelector('.c-fileRow.sd-view-doc');
      if (fileRow) openDocViewer(fileRow);
    };
  }

  const showDetailsBtn = toolbar.querySelector('.js-selectionShowDetails');
  if (showDetailsBtn) {
    showDetailsBtn.onclick = (e) => {
      e.preventDefault();
      const [row] = selectedRows.values();
      const link = row && row.querySelector('.js-rowShowLink');
      if (link) link.click();
    };
  }

  const resendReminderBtn = toolbar.querySelector('.js-selectionResendReminder');
  if (resendReminderBtn) {
    resendReminderBtn.onclick = (e) => {
      e.preventDefault();
      const [row] = selectedRows.values();
      const link = row && row.querySelector('.js-rowResendReminderLink');
      if (link) link.click();
    };
  }

  const downloadLink = toolbar.querySelector('.js-selectionDownload');
  if (downloadLink) {
    downloadLink.onclick = (e) => {
      e.preventDefault();

      const items = Array.from(selectedRows.values());
      if (items.length === 0) return;

      // A single selected file downloads directly via its own existing link --
      // no zip wrapper, and no confirm/notice here (that link has its own
      // confirm already, and there's nothing to compress). Everything else --
      // a single folder, or 2+ items of either type -- hits the bulk_download
      // endpoint, which always zips (folders' contents nested under their own
      // folder path, see ZipsSiteDocuments#zip_site_documents_with_folder_structure).
      if (items.length === 1 && items[0].dataset.itemType === 'file') {
        const link = items[0].querySelector('.js-rowDownloadLink');
        if (link) link.click();
        return;
      }

      if (
        !window.confirm(
          'Are you sure you want to download the selected files and/or folders?'
        )
      ) {
        return;
      }

      createNotice('Compressing the files... Please wait.');

      const basePath = toolbar.dataset.bulkDownloadPath;
      if (!basePath) return;

      const fileIds = items
        .filter(row => row.dataset.itemType === 'file')
        .map(row => row.dataset.itemId);
      const folderIds = items
        .filter(row => row.dataset.itemType === 'folder')
        .map(row => row.dataset.itemId);

      const query = [
        ...fileIds.map(id => `ids[]=${encodeURIComponent(id)}`),
        ...folderIds.map(id => `folder_ids[]=${encodeURIComponent(id)}`),
      ].join('&');

      window.location.href = `${basePath}?${query}`;
    };
  }
}

function bindRowSelection() {
  selectedRows = new Map();
  bindSelectionToolbarActions();
  renderSelectionToolbar();
}

function bindFolderNavigation() {
  document.querySelectorAll('.sd-open-folder').forEach(el => {
    const target = el.closest('tr') || el;

    bindRowOpenAndSelect(target, () => Turbolinks.visit(el.dataset.path));
  });
}

// Collapses the page heading's breadcrumb -- first to "... > second-to-last >
// last", then, if even that's still too wide (e.g. a single long folder name),
// down to "... > last" -- never on a fixed breakpoint, always based on each
// state's actual rendered width vs the space actually available. The states
// are rendered in least- to most-collapsed order, so the loop below tries
// each in turn and stops at the first that fits, falling back to the most
// collapsed one (the last in the list) if none of them do.
const MIN_SEGMENTS_BEFORE_FORCED_COLLAPSE = 4;

function evaluateHeadingCollapse() {
  const heading = document.querySelector('[data-site-docs-heading]');
  if (!heading) return;

  const states = Array.from(heading.querySelectorAll('[data-heading-state]'));
  if (states.length === 0) return;

  // A path this deep reads as noise even when it technically still fits the
  // available width, so the "full" state is skipped outright rather than
  // waiting for a real overflow to force collapsing.
  const segmentCount = Number(heading.dataset.segmentCount) || 0;
  const candidates = segmentCount >= MIN_SEGMENTS_BEFORE_FORCED_COLLAPSE
    ? states.filter(state => state.dataset.headingState !== 'full')
    : states;

  for (const state of candidates) {
    // A hidden (display: none) element always reports a scrollWidth of 0, so
    // every other state must be hidden and this one shown before measuring it.
    states.forEach(other => {
      other.style.display = 'none';
    });
    state.style.display = 'flex';

    if (state.scrollWidth <= heading.clientWidth) break;
  }
}

function bindHeadingCollapse() {
  evaluateHeadingCollapse();

  if (!headingResizeListenerBound) {
    window.addEventListener('resize', debounce(evaluateHeadingCollapse, 150));
    headingResizeListenerBound = true;
  }
}

// The link itself carries `download` (see _table_actions.html.erb) so
// Turbolinks leaves the click alone and the browser handles the download
// natively -- the page never navigates away, so this notice stays on screen
// as reassurance while the server zips everything up. rails-ujs (bound in
// packs/shared.js) fires this event right after its own data-confirm dialog
// resolves; detail[0] is the boolean the user answered with.
function bindDownloadAllConfirm() {
  document.querySelectorAll('.js-downloadAllLink').forEach(link => {
    link.addEventListener('confirm:complete', event => {
      if (event.detail[0]) {
        createNotice('Compressing the files... Please wait.');
      }
    });
  });
}

// Same shape as bindDownloadAllConfirm above -- `download` keeps Turbolinks
// out of the way and the browser handles the download natively, so a notice
// is the only "something's happening" feedback the page can give (there's no
// confirm dialog here to hang the notice off, so it's just a plain click).
// Also guards against a second click firing an overlapping duplicate
// download while the report is still generating -- each request regenerates
// the PDF fresh, so there's nothing to be gained from letting it double up.
function bindExportSignatureReport() {
  document.querySelectorAll('.js-exportSignatureReport').forEach(link => {
    link.addEventListener('click', event => {
      if (link.classList.contains('is-disabled')) {
        event.preventDefault();
        return;
      }

      createNotice('Generating report... Please wait.');

      link.classList.add('is-disabled');
      setTimeout(() => link.classList.remove('is-disabled'), 5000);
    });
  });
}

function bindNewFolderModal() {
  document.querySelectorAll('.js-openNewFolderModal').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      closeAllMenus();
      closeAllDrawers();
      showOverlay('new-folder-modal');
    };
  });

  document.querySelectorAll('[data-action="sd-close-new-folder"]').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      hideOverlay('new-folder-modal');
    };
  });
}

// The "View Signature" overflow action on a document's Recipients table --
// only ever rendered for an operative who has actually signed (see
// site_docs/show.html.erb), so there's no "not yet signed" state to handle here.
function bindViewSignatureModal() {
  document.querySelectorAll('.js-openViewSignatureModal').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      closeAllMenus();
      closeAllDrawers();

      document.getElementById('sig-operativeName').textContent = el.dataset.operativeName;
      document.getElementById('sig-signedAt').textContent = el.dataset.signedAt;

      const image = document.getElementById('sig-image');
      const loading = document.getElementById('sig-imageLoading');

      image.classList.add('u-hide');
      loading.classList.remove('u-hide');

      image.onload = () => {
        loading.classList.add('u-hide');
        image.classList.remove('u-hide');
      };
      image.onerror = image.onload;

      if (image.getAttribute('src') === el.dataset.image) {
        // Same URL the <img> already has -- setting it again is a no-op in
        // most browsers (no load/error event fires), so the spinner would
        // otherwise never clear on a repeat view of the same signature.
        image.onload();
      } else {
        image.setAttribute('src', el.dataset.image);
      }

      showOverlay('signature-modal');
    };
  });

  document.querySelectorAll('[data-action="sig-close-signature"]').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      hideOverlay('signature-modal');
    };
  });
}

function bindRenameFolderModal() {
  const form = document.getElementById('rename-folder-form');
  const nameField = document.getElementById('rename-folder-name');
  const paths = sitePaths();

  document.querySelectorAll('.js-openRenameModal').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      closeAllMenus();
      closeAllDrawers();

      if (form && paths) form.action = `${paths.siteDocumentFolders}/${el.dataset.folderId}`;
      if (nameField) nameField.value = el.dataset.folderName;

      showOverlay('rename-folder-modal');
    };
  });

  document.querySelectorAll('[data-action="sd-close-rename-folder"]').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      hideOverlay('rename-folder-modal');
    };
  });
}

// Shared by both the drag-and-drop drop handler and the "Move to..." modal's
// confirm button — a single call site for the move request, regardless of which
// front-end trigger initiated it.
function performMove(item, destinationFolderId) {
  const paths = sitePaths();
  if (!paths || !item) return;

  const endpoint = item.type === 'folder'
    ? `${paths.siteDocumentFolders}/${item.id}/move`
    : `${paths.siteDocs}/${item.id}/move`;

  fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ folder_id: destinationFolderId || null }),
  }).then(res => res.json()).then(handleMoveResult);
}

function handleMoveResult(data) {
  if (data.success) {
    // The move itself always succeeds even when the name collided (see
    // SiteDocument/SiteDocumentFolder#dedupe_name_within_*) -- this is the
    // only place that can tell the user it happened, since the destination's
    // own row will just show up already renamed after the reload below.
    if (data.renamed_from) {
      window.alert(`"${data.renamed_from}" already existed in the destination folder, so it was renamed to "${data.name}".`);
    }

    Turbolinks.visit(window.location.pathname + window.location.search);
  } else if (data.error) {
    window.alert(data.error);
  }
}

// Counterpart to performMove for moving more than one item at once -- groups
// the given items by type and hits the two new bulk endpoints (at most one
// request per type, since a selection can mix files and folders), then
// reloads and/or alerts with the aggregated failures. Used by the toolbar's
// "Move to..." action, and by drag-and-drop when the dragged row is part of
// a multi-row selection (see bindDragAndDrop below). The existing
// single-item performMove/move endpoints above are still used by the
// per-row "Move to..." action and by single-row drag-and-drop.
function performBulkMove(items, destinationFolderId) {
  const paths = sitePaths();
  if (!paths || items.length === 0) return;

  Promise.all(bulkRequestsByType(items, paths, 'bulk_move', (endpoint, ids) => fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ ids, folder_id: destinationFolderId || null }),
  }))).then(handleBulkResult);
}

// Toolbar-only bulk delete -- see performBulkMove above for why this groups by
// type and hits (at most) two endpoints instead of one. The existing per-row
// delete link is untouched.
function performBulkDestroy(items) {
  const paths = sitePaths();
  if (!paths || items.length === 0) return;

  Promise.all(bulkRequestsByType(items, paths, 'bulk_destroy', (endpoint, ids) => {
    const query = ids.map(id => `ids[]=${encodeURIComponent(id)}`).join('&');

    return fetch(`${endpoint}?${query}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
    });
  })).then(handleBulkResult);
}

// Splits a mixed file/folder selection into (up to) one fetch per type against
// the given bulk action name, via the caller's own request-building function.
function bulkRequestsByType(items, paths, action, buildRequest) {
  const idsByType = {
    file: items.filter(item => item.type === 'file').map(item => item.id),
    folder: items.filter(item => item.type === 'folder').map(item => item.id),
  };

  return Object.entries(idsByType)
    .filter(([, ids]) => ids.length > 0)
    .map(([type, ids]) => {
      const endpoint = type === 'folder'
        ? `${paths.siteDocumentFolders}/${action}`
        : `${paths.siteDocs}/${action}`;

      return buildRequest(endpoint, ids).then(res => res.json());
    });
}

// Shared by performBulkMove/performBulkDestroy: reloads to reflect whatever
// succeeded, and separately surfaces any per-item failures -- since a bulk
// action is often a partial outcome, this always reloads (unlike
// handleMoveResult's single-item all-or-nothing behavior above). `renamed`
// is only ever present on bulk_move responses (bulk_destroy has nothing to
// rename) -- see handleMoveResult for why this is the only place that can
// tell the user a move-time name collision happened.
function handleBulkResult(results) {
  const failed = results.flatMap(result => result.failed || []);
  const renamed = results.flatMap(result => result.renamed || []);

  const messages = [
    ...renamed.map(item => `"${item.from}" already existed in the destination folder, so it was renamed to "${item.to}".`),
    ...failed.map(item => `${item.name}: ${item.error}`),
  ];

  if (messages.length > 0) {
    window.alert(messages.join(' '));
  }

  Turbolinks.visit(window.location.pathname + window.location.search);
}

function bindMoveToModal() {
  let pendingPerformMove = null;
  let selectedFolderId = null;

  const paths = sitePaths();
  const list = document.getElementById('move-to-folder-list');
  const emptyMessage = list ? list.querySelector('.js-moveEmpty') : null;
  const confirmBtn = document.getElementById('move-to-confirm');
  const itemNameLabel = document.getElementById('move-to-item-name');

  const selectFolder = (folderId, rowEl) => {
    list.querySelectorAll('.c-siteDocs__moveItem').forEach(node => node.classList.remove('is-selected'));
    rowEl.classList.add('is-selected');
    selectedFolderId = folderId;
    if (confirmBtn) confirmBtn.disabled = false;
  };

  // items: [{ type, id, siteId }, ...] -- always an array, even for the
  // per-row single-item trigger, so the exclude-list/site-scoping logic below
  // doesn't need a separate branch for "one item" vs "a selection".
  const openMoveModal = (items, label, performFn) => {
    if (!paths || items.length === 0) return;

    closeAllMenus();
    closeAllDrawers();

    pendingPerformMove = performFn;
    selectedFolderId = null;

    if (itemNameLabel) itemNameLabel.textContent = label;
    if (confirmBtn) confirmBtn.disabled = true;

    if (list) {
      list.querySelectorAll('.c-siteDocs__moveItem').forEach(node => node.remove());
      if (emptyMessage) emptyMessage.hidden = true;
    }

    // Excludes a selected folder from being offered as its own destination,
    // and the page's current folder (moving something to where it already is
    // is a no-op). Folders being moved are also sent as exclude_descendants_of
    // so the list doesn't offer one of their own subfolders either -- moving
    // into your own descendant is still rejected server-side in move/bulk_move
    // regardless of what this list offers, but there's no reason to let the
    // picker suggest a destination that can only ever fail.
    const currentFolderId = paths.currentFolderId;
    const movedFolderIds = items.filter(item => item.type === 'folder').map(item => item.id);
    const excludeIds = [...movedFolderIds, currentFolderId].filter(Boolean);
    const excludeParam = excludeIds.map(id => `&exclude_ids[]=${id}`).join('')
      + movedFolderIds.map(id => `&exclude_descendants_of[]=${id}`).join('');

    fetch(`${paths.siteDocumentFolders}?site_id=${items[0].siteId}${excludeParam}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(res => res.json())
      .then(folderOptions => {
        if (!list) return;

        if (folderOptions.length === 0) {
          if (emptyMessage) emptyMessage.hidden = false;
          return;
        }

        folderOptions.forEach(folder => {
          const row = document.createElement('div');
          row.className = 'c-siteDocs__moveItem';
          row.dataset.folderId = folder.id;
          row.onclick = () => selectFolder(folder.id, row);

          const name = document.createElement('span');
          name.textContent = folder.name;

          row.appendChild(folderIcon());
          row.appendChild(name);
          list.appendChild(row);
        });
      });

    showOverlay('move-to-modal');
  };

  document.querySelectorAll('.js-openMoveModal').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();

      const item = {
        type: el.dataset.itemType,
        id: el.dataset.itemId,
        siteId: el.dataset.itemSiteId,
      };

      openMoveModal([item], el.dataset.itemName, (destinationFolderId) => performMove(item, destinationFolderId));
    };
  });

  document.querySelectorAll('.js-selectionMove').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();

      const items = Array.from(selectedRows.values()).map(row => ({
        type: row.dataset.itemType,
        id: row.dataset.itemId,
        siteId: row.dataset.itemSiteId,
      }));
      if (items.length === 0) return;

      const label = `${items.length} selected item${items.length === 1 ? '' : 's'}`;

      openMoveModal(items, label, (destinationFolderId) => performBulkMove(items, destinationFolderId));
    };
  });

  document.querySelectorAll('[data-action="sd-close-move-to"]').forEach(el => {
    el.onclick = (e) => {
      e.preventDefault();
      hideOverlay('move-to-modal');
    };
  });

  if (confirmBtn) {
    confirmBtn.onclick = (e) => {
      e.preventDefault();
      if (!selectedFolderId || !pendingPerformMove) return;

      hideOverlay('move-to-modal');
      pendingPerformMove(selectedFolderId);
      pendingPerformMove = null;
    };
  }
}

function bindDragAndDrop() {
  let draggedItem = null;

  document.querySelectorAll('.js-draggable').forEach(el => {
    el.addEventListener('dragstart', (e) => {
      draggedItem = {
        type: el.dataset.itemType,
        id: el.dataset.itemId,
        siteId: el.dataset.itemSiteId,
      };

      // Required for the drag to actually proceed in every browser (Firefox in
      // particular refuses to start a drag at all without it) -- unused otherwise,
      // since drop reads the draggedItem closure above rather than this payload.
      // Dragging a plain <img> (see .c-fileRow__thumb) "worked" without this only
      // because browsers auto-populate dataTransfer themselves for native image
      // drags; starting from anywhere else went through this handler alone, which
      // never registered any data, so the browser silently refused to start.
      e.dataTransfer.setData('text/plain', el.dataset.itemId);
      e.dataTransfer.effectAllowed = 'move';
    });
  });

  document.querySelectorAll('.js-dropTarget').forEach(el => {
    el.addEventListener('dragover', (e) => {
      if (!draggedItem) return;
      if (draggedItem.type === 'folder' && draggedItem.id === el.dataset.dropTargetId) return;

      e.preventDefault();
      el.classList.add('is-dragOver');
    });

    el.addEventListener('dragleave', () => el.classList.remove('is-dragOver'));

    el.addEventListener('drop', (e) => {
      e.preventDefault();
      el.classList.remove('is-dragOver');
      if (!draggedItem) return;

      // If the dragged row is part of a multi-row selection, move the whole
      // selection (bulk_move) rather than just the row the drag happened to
      // start on -- otherwise dragging one of several selected rows silently
      // drops the rest. A drag on a row outside the current selection still
      // moves just that one row.
      const draggedKey = `${draggedItem.type}:${draggedItem.id}`;

      if (selectedRows.size > 1 && selectedRows.has(draggedKey)) {
        const items = Array.from(selectedRows.values()).map(row => ({
          type: row.dataset.itemType,
          id: row.dataset.itemId,
        }));

        performBulkMove(items, el.dataset.dropTargetId);
      } else {
        performMove(draggedItem, el.dataset.dropTargetId);
      }

      draggedItem = null;
    });
  });
}
